import { memo, useMemo } from 'react';
import { type Chat, EngagementType, type InteractionType, InteractionTypeEnum } from '@trinity/chat';
import { isReactiveCallCenterInteraction, isReactiveMslInteraction } from '@trinity/chat-components/components/Chat/utils';
import { useGroupFeatures } from '@trinity/chat-components/containers/Chat';
import { toTitleCase } from '@trinity/chat-components/utils';
import { CitationsTable } from './Citation.Table';
import { buildEngagementTableItem } from './utils';

type CrmNoteProps<T extends InteractionType = InteractionType> = {
  data: Chat.CrmNoteCitationMetadata<T>;
};

export const CrmNote = memo(({ data }: CrmNoteProps) => {

  switch (data.type) {
    case InteractionTypeEnum.CallCenterInteraction:
      return <CallCenterInteraction data={data} />;
    case InteractionTypeEnum.MedicalInsight:
      return <MedicalInsight data={data} />;
    case InteractionTypeEnum.DocumentedInterest:
      return <DocumentedInterest data={data} />;
    case InteractionTypeEnum.FbmsInteraction:
      return <FbmsInteraction data={data} />;
    default:
      throw new UnreachableCaseError(data);
  }
});

const FbmsInteraction = memo(({ data }: CrmNoteProps<InteractionTypeEnum.FbmsInteraction>) => {
  const features = useGroupFeatures();
  const engagementResponseLabels = features.engagementResponseLabels;

  const items = useMemo(() => {
    const educationGapValue = data.munQuestion1
      ? data.munQuestion3
        ? `${data.munQuestion1} - ${data.munQuestion3}`
        : data.munQuestion1
      : null;

    return [
      {
        name: 'MSL',
        value: data.accountName,
      },
      {
        name: 'HCP Name',
        value: data.kolName,
      },
      {
        name: 'Product',
        value: data.productName,
      },
      {
        name: 'Franchise',
        value: data.franchise,
      },
      educationGapValue && {
        name: 'Education Gap',
        value: educationGapValue,
      },
      {
        name: 'Source',
        value: data.source,
      },
      {
        name: 'Table',
        value: data.table,
      },
      engagementResponseLabels && buildEngagementTableItem(isReactiveMslInteraction(data) ? EngagementType.Reactive : EngagementType.Proactive),
    ].filter(Boolean);
  }, [data, engagementResponseLabels]);

  return (
    <CitationsTable items={items} />
  );
});

const CallCenterInteraction = memo(({ data }: CrmNoteProps<InteractionTypeEnum.CallCenterInteraction>) => {
  const features = useGroupFeatures();
  const engagementResponseLabels = features.engagementResponseLabels;

  const items = useMemo(() => {
    return [
      {
        name: 'HCP Name',
        value: data.kolName,
      },
      {
        name: 'Product',
        value: data.productName,
      },
      {
        name: 'Topic',
        value: data.therapeuticArea,
      },
      data.interactionSubType === 'MIR' && {
        name: 'Request',
        value: data.request,
      },
      {
        name: 'Source',
        value: `${data.src} (${data.subSrc})`,
      },
      data.category && {
        name: 'Category',
        value: data.category + (data.subCategory ? ` (${data.subCategory})` : ''),
      },
      {
        name: 'Table',
        value: data.table,
      },
      engagementResponseLabels && buildEngagementTableItem(isReactiveCallCenterInteraction(data) ? EngagementType.Reactive : EngagementType.Proactive),
    ].filter(Boolean);
  }, [data, engagementResponseLabels]);

  return (
    <CitationsTable items={items} />
  );
});

const DocumentedInterest = memo(({ data }: CrmNoteProps<InteractionTypeEnum.DocumentedInterest>) => {
  const features = useGroupFeatures();
  const engagementResponseLabels = features.engagementResponseLabels;

  const items = useMemo(() => {
    return [
      {
        name: 'MSL',
        value: data.accountName,
      },
      {
        name: 'HCP Name',
        value: data.kolName,
      },
      {
        name: 'Product',
        value: data.productName,
      },
      {
        name: 'Interest Areas',
        value: data.interestAreas,
      },
      {
        name: 'Interest Verbatim',
        value: data.interest,
      },
      {
        name: 'Source',
        value: data.source,
      },
      {
        name: 'Table',
        value: data.table,
      },
      engagementResponseLabels && buildEngagementTableItem(EngagementType.Reactive),
    ];
  }, [data.accountName, data.interest, data.interestAreas, data.kolName, data.productName, data.source, data.table, engagementResponseLabels]);

  return (
    <CitationsTable items={items} />
  );
});

const MedicalInsight = memo(({ data }: CrmNoteProps<InteractionTypeEnum.MedicalInsight>) => {
  const features = useGroupFeatures();
  const engagementResponseLabels = features.engagementResponseLabels;

  const items = useMemo(() => {
    return [
      {
        name: 'MSL',
        value: data.accountName,
      },
      {
        name: 'HCP Name',
        value: data.kolName,
      },
      {
        name: 'Topics',
        value: [data.therapeuticArea, data.therapeuticSubArea].join(', '),
      },
      {
        name: 'Product',
        value: data.product,
      },
      {
        name: 'Sentiment',
        value: toTitleCase(data.sentiment),
      },
      {
        name: 'Insight',
        value: data.note,
      },
      {
        name: 'Source',
        value: data.source,
      },
      {
        name: 'Table',
        value: data.table,
      },
      engagementResponseLabels && buildEngagementTableItem(EngagementType.None),
    ].filter(Boolean);
  }, [data, engagementResponseLabels]);

  return (
    <CitationsTable items={items} />
  );
});